import type { Relayer } from "../config/relayers";

export function getRandomRelayer(relayers: Relayer[]): Relayer {
  if (!relayers.length) {
    throw new Error("arcane-privacy-sdk: no relayer available");
  }

  const index = Math.floor(Math.random() * relayers.length);
  return relayers[index]!;
}

export function getRelayerByIndex(relayers: Relayer[], relayerIdx: number): Relayer {
  if (!relayers.length) {
    throw new Error("arcane-privacy-sdk: no relayer available");
  }

  if (!Number.isInteger(relayerIdx) || relayerIdx < 0 || relayerIdx >= relayers.length) {
    throw new Error(
      `arcane-privacy-sdk: relayerIdx ${relayerIdx} is out of range (0-${relayers.length - 1})`
    );
  }

  return relayers[relayerIdx]!;
}

export function selectRelayer(
  relayers: Relayer[],
  relayerIdx?: number
): Relayer {
  if (relayerIdx === undefined) {
    return getRandomRelayer(relayers);
  }

  return getRelayerByIndex(relayers, relayerIdx);
}

export function calculateWithdrawFee(
  amount: string,
  relayerFee: unknown,
  platformFeePercent: string
): bigint {
  const amountBn = BigInt(amount);
  const relayerFeeBps = BigInt(Math.round(Number(relayerFee) * 100));
  const platformFeeBps = BigInt(Math.round(parseFloat(platformFeePercent) * 100));
  return (amountBn * (relayerFeeBps + platformFeeBps)) / 10000n;
}

type RelayerJob = {
  status: string;
  txHash?: string;
  errorMessage?: string;
};

export type WithdrawJobPollResult = {
  txHash: string;
  status: "success" | "failed";
};

export async function pollWithdrawJob(
  relayerUrl: string,
  jobId: string,
  maxWaitMs = 120_000
): Promise<WithdrawJobPollResult> {
  const start = Date.now();

  while (Date.now() - start < maxWaitMs) {
    const res = await fetch(`${relayerUrl}/v1/jobs/${jobId}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || "Withdrawal failed");
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || "Withdrawal failed");
    }

    const job = (await res.json()) as RelayerJob;

    if (job.status === "CONFIRMED") {
      if (!job.txHash) {
        throw new Error("Withdrawal confirmed but txHash is missing");
      }
      return { txHash: job.txHash, status: "success" };
    }

    if (job.status === "FAILED") {
      return {
        txHash: job.txHash ?? "",
        status: "failed",
      };
    }

    if (
      job.status === "ACCEPTED" ||
      job.status === "SENT" ||
      job.status === "PENDING" ||
      job.status === 'QUEUED'
    ) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      continue;
    }

    await new Promise((resolve) => setTimeout(resolve, 3000));
  }

  throw new Error("Withdrawal timed out");
}
